import React from 'react';

/**
 * SegmentFilter
 *
 * Renders one collapsible section per screener question that has a tag_key.
 * Within each dimension: OR logic (any selected value passes).
 * Between dimensions: AND logic (must pass all dimensions).
 *
 * Props:
 *   dimensions   — [{ key, label, values: [{ value, label }] }]
 *   activeFilters — { [key]: string[] }
 *   onChange     — (key, values) => void
 *   sessionCount  — total sessions currently shown
 */
const SegmentFilter = ({ dimensions, activeFilters, onChange, sessionCount }) => {
  const totalActive = Object.values(activeFilters).flat().length;

  const toggleValue = (key, value) => {
    const current = activeFilters[key] || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onChange(key, next);
  };

  const clearAll = () => {
    dimensions.forEach(d => onChange(d.key, []));
  };

  return (
    <div className="bg-white border-r border-gray-200 flex flex-col" style={{ width: 220, minWidth: 220 }}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Filter by segment</p>
          {sessionCount !== undefined && (
            <p className="text-[10px] text-gray-300 font-bold mt-0.5">{sessionCount} session{sessionCount !== 1 ? 's' : ''}</p>
          )}
        </div>
        {totalActive > 0 && (
          <button
            onClick={clearAll}
            className="text-[9px] font-black text-rutabaga-pink uppercase tracking-widest hover:text-rutabaga-pink-hover transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Dimensions */}
      <div className="flex-1 overflow-y-auto">
        {dimensions.length === 0 && (
          <p className="text-[10px] text-gray-300 font-bold text-center p-4">
            No screener questions with segment tags configured.
          </p>
        )}

        {dimensions.map(dim => {
          const selected = activeFilters[dim.key] || [];
          return (
            <div key={dim.key} className="border-b border-gray-50 last:border-0">
              <div className="px-4 py-2.5">
                <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-2 leading-tight">
                  {dim.label}
                  {selected.length > 0 && (
                    <span className="ml-1.5 bg-rutabaga-pink/10 text-rutabaga-pink px-1.5 py-0.5 rounded-full text-[8px]">
                      {selected.length}
                    </span>
                  )}
                </p>
                <div className="space-y-1.5">
                  {dim.values.map(({ value, label }) => {
                    const isSelected = selected.includes(value);
                    return (
                      <label key={value} className="flex items-center gap-2 cursor-pointer group">
                        <div
                          onClick={() => toggleValue(dim.key, value)}
                          className={`w-3.5 h-3.5 rounded flex items-center justify-center border shrink-0 transition-colors
                            ${isSelected ? 'bg-rutabaga-pink border-rutabaga-pink' : 'border-gray-300 group-hover:border-rutabaga-pink/50'}`}
                        >
                          {isSelected && (
                            <svg viewBox="0 0 8 8" width="8" height="8">
                              <path d="M1 4L3 6L7 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                            </svg>
                          )}
                        </div>
                        <span
                          onClick={() => toggleValue(dim.key, value)}
                          className={`text-[10px] font-bold transition-colors leading-tight
                            ${isSelected ? 'text-rutabaga-dark' : 'text-gray-400 group-hover:text-gray-600'}`}
                        >
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentFilter;
